import { useRef, useState, type ChangeEvent } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { FileText, Upload, X } from 'lucide-react';
import { Button, Spinner, Card } from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import { uploadAttachment, type AttachmentPayload } from '@/api/uploads';
import { ConfigToggleHeader } from './config-toggle-header';
import type { ContractFormValues } from '../contract-form';

/** Storage prefix for contract NDA attachments (legacy `contract-nda`). */
const NDA_PREFIX = 'contract-nda';
const ACCEPT = '.pdf,.doc,.docx';
const ACCEPTED_EXT = ['pdf', 'doc', 'docx'];

/**
 * NDA configuration — a switch (`nda_enable`) reveals a file picker. Each chosen
 * `.pdf/.doc/.docx` uploads via the presigned-URL flow (prefix `contract-nda`)
 * and its attachment record is appended to `nda_attachments`; uploads can be
 * removed. Mirrors legacy NDAConfigSection (drag/drop trimmed to a click picker).
 */
export function NdaConfigSection() {
  const { control, setValue } = useFormContext<ContractFormValues>();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const enabled = useWatch({ control, name: 'nda_enable' });
  const attachments = useWatch({ control, name: 'nda_attachments' }) ?? [];

  const handleFiles = async (files: File[]) => {
    const valid = files.filter((f) => ACCEPTED_EXT.includes(f.name.split('.').pop()?.toLowerCase() ?? ''));
    if (valid.length === 0) {
      toast.error('Unsupported file type', { description: 'Only PDF, DOC, and DOCX files are allowed.' });
      return;
    }
    if (valid.length < files.length) {
      toast.warning('Some files were skipped', { description: 'Only PDF, DOC, and DOCX files are allowed.' });
    }
    setUploading(true);
    try {
      const uploaded: AttachmentPayload[] = [];
      for (const file of valid) uploaded.push(await uploadAttachment(file, NDA_PREFIX));
      setValue('nda_attachments', [...attachments, ...uploaded], { shouldDirty: true });
      toast.success(uploaded.length > 1 ? 'Files uploaded' : 'File uploaded');
    } catch (e) {
      const description = e instanceof Error ? e.message : 'Please try again.';
      toast.error('Upload failed', { description });
    } finally {
      setUploading(false);
    }
  };

  const onChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;
    await handleFiles(Array.from(e.target.files));
    if (inputRef.current) inputRef.current.value = '';
  };

  const removeAt = (i: number) => {
    setValue('nda_attachments', attachments.filter((_, idx) => idx !== i), { shouldDirty: true });
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
      <ConfigToggleHeader
        name="nda_enable"
        icon={<FileText size={16} />}
        title="Enable NDA"
        description="Flag this contract as covered by an NDA and attach the document."
      />

      {enabled ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            multiple
            style={{ display: 'none' }}
            onChange={onChange}
          />
          <Button
            type="button"
            variant="secondary"
            startIcon={uploading ? <Spinner size="sm" /> : <Upload size={15} />}
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
          >
            {uploading ? 'Uploading…' : 'Upload NDA (PDF, DOC, DOCX)'}
          </Button>

          {attachments.length > 0 ? (
            <ul style={{ display: 'grid', gap: 'var(--vn-space-2)', listStyle: 'none', margin: 0, padding: 0 }}>
              {attachments.map((att, i) => (
                <li key={`${att.attachment_file_url}-${i}`}>
                  <Card flat style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--vn-space-3)', padding: 'var(--vn-space-2) var(--vn-space-3)' }}>
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--vn-space-2)',
                      fontSize: 'var(--vn-font-size-sm)',
                      minWidth: 0,
                    }}
                  >
                    <FileText size={14} style={{ flexShrink: 0, color: 'var(--vn-fg-muted)' }} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {att.attachment_file_name}
                    </span>
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    startIcon={<X size={14} />}
                    onClick={() => removeAt(i)}
                    aria-label={`Remove ${att.attachment_file_name}`}
                  />
                  </Card>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
